/**
 * Utility functions for calculating course progress statistics
 */

/**
 * Counts the number of completed courses from a courseProgressById map
 * A course is considered completed if progress === 100
 */
export function getCompletedCoursesCount(
    courseProgressById: Record<string, number | null | undefined>,
): number {
    return Object.values(courseProgressById).filter(
        (progress) => typeof progress === 'number' && progress === 100,
    ).length;
}

/**
 * Finds the next course to show: first any course in progress (started but not completed),
 * otherwise the first course not yet started.
 * Returns the course URL, 1-based course number, and progress.
 * Returns undefined values when all courses are completed.
 */
export function findFirstIncompleteCourse(
    courseIds: string[],
    courseProgressById: Record<string, number | null | undefined>,
    courseTakingById: Record<string, string | undefined>,
): {
    nextCourseUrl: string | undefined;
    nextCourseNumber: number | undefined;
    nextCourseProgress: number | undefined;
} {
    const inProgressIndex = courseIds.findIndex((courseId) => {
        const progress = courseProgressById[courseId];
        return typeof progress === 'number' && progress > 0 && progress < 100;
    });

    if (inProgressIndex !== -1) {
        const nextCourseId = courseIds[inProgressIndex];
        const progress = courseProgressById[nextCourseId] as number;
        return {
            nextCourseUrl: courseTakingById[nextCourseId],
            nextCourseNumber: inProgressIndex + 1,
            nextCourseProgress: progress,
        };
    }

    const firstIncompleteIndex = courseIds.findIndex((courseId) => {
        const progress = courseProgressById[courseId];
        return typeof progress !== 'number' || progress < 100;
    });

    if (firstIncompleteIndex === -1) {
        return {
            nextCourseUrl: undefined,
            nextCourseNumber: undefined,
            nextCourseProgress: undefined,
        };
    }

    const nextCourseId = courseIds[firstIncompleteIndex];
    const progress = courseProgressById[nextCourseId];
    const nextCourseProgress = typeof progress === 'number' ? progress : 0;

    return {
        nextCourseUrl: courseTakingById[nextCourseId],
        nextCourseNumber: firstIncompleteIndex + 1,
        nextCourseProgress,
    };
}
